import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface MenuTag {
  // Background Color
  color: string;
  value: string;
}

export interface MenuChildrenItem {
  children?: MenuChildrenItem[];
  name: string;
  route: string;
  type: 'link' | 'sub' | 'extLink' | 'extTabLink';
}

export interface Menu {
  badge?: MenuTag;
  children?: MenuChildrenItem[];
  icon: string;
  label?: MenuTag;
  name: string;
  route: string;
  type: 'link' | 'sub' | 'extLink' | 'extTabLink';
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menu$: BehaviorSubject<Menu[]> = new BehaviorSubject<Menu[]>([]);

  public static recursiveMenuChildrenForTranslation(
    children: MenuChildrenItem[],
    namespace: string
  ): MenuChildrenItem[] {
    const clonedMenu: MenuChildrenItem[] = MenuService.deepCopyJson(children);

    for (const menuItem of clonedMenu) {
      menuItem.name = `${namespace}.${menuItem.name}`;
      if (menuItem.children && menuItem.children.length > 0) {
        this.recursiveMenuChildrenForTranslation(menuItem.children, menuItem.name);
      }
    }

    return clonedMenu;
  }

  public static recursiveMenuForTranslation(menu: Menu[], namespace: string): Menu[] {
    const clonedMenu: Menu[] = MenuService.deepCopyJson(menu);

    for (const menuItem of clonedMenu) {
      menuItem.name = `${namespace}.${menuItem.name}`;
      if (menuItem.children && menuItem.children.length > 0) {
        menuItem.children = this.recursiveMenuChildrenForTranslation(
          menuItem.children,
          menuItem.name
        );
      }
    }

    return clonedMenu;
  }

  private static deepCopyJson<T>(object: T): T {
    return JSON.parse(JSON.stringify(object));
  }

  private static isLeafItem(item: MenuChildrenItem): boolean {
    const cond0 = item.route === undefined;
    const cond1 = item.children === undefined;
    const cond2 = !cond1 && item.children?.length === 0;
    return cond0 || cond1 || cond2;
  }

  private static jsonDeepEqual(objectA: any, objectB: any): boolean {
    const cond = JSON.stringify(objectA) === JSON.stringify(objectB);
    return cond;
  }

  private static routeEqual(routeArr: Array<string>, realRouteArr: Array<string>): boolean {
    let realRouteArrTmp = MenuService.deepCopyJson(realRouteArr);
    realRouteArrTmp = realRouteArrTmp.filter(r => r !== '');
    return MenuService.jsonDeepEqual(routeArr, realRouteArrTmp);
  }

  public add(menu: Menu) {
    const tmpMenu = this.menu$.value;
    tmpMenu.push(menu);
    this.menu$.next(tmpMenu);
  }

  public getAll(): Observable<Menu[]> {
    return this.menu$.asObservable();
  }

  public getMenuLevel(routeArr: string[]): string[] {
    let tmpArr: string[] = [];
    this.menu$.value.forEach(item => {
      /// / breadth-first-traverse -modified
      interface MenuLayer {
        item: Menu | MenuChildrenItem;
        parentNamePathList: string[];
        realRouteArr: string[];
      }

      let unhandledLayer: MenuLayer[] = [{ item, parentNamePathList: [], realRouteArr: [] }];
      while (unhandledLayer.length > 0) {
        let nextUnhandledLayer: MenuLayer[] = [];
        for (const ele of unhandledLayer) {
          const eachItem = ele.item;
          const currentNamePathList: string[] = MenuService.deepCopyJson(
            ele.parentNamePathList
          ).concat(eachItem.name);
          const currentRealRouteArr: string[] = MenuService.deepCopyJson(ele.realRouteArr).concat(
            eachItem.route
          );
          // Compare the full Array for expandable
          const cond = MenuService.routeEqual(routeArr, currentRealRouteArr);
          if (cond) {
            tmpArr = currentNamePathList;
            break;
          }

          const isLeafCond = MenuService.isLeafItem(eachItem);
          if (!isLeafCond) {
            const { children } = eachItem;
            const wrappedChildren = (children || []).map(child => ({
              item: child,
              parentNamePathList: currentNamePathList,
              realRouteArr: currentRealRouteArr,
            }));
            nextUnhandledLayer = nextUnhandledLayer.concat(wrappedChildren);
          }
        }
        unhandledLayer = nextUnhandledLayer;
      }
    });
    return tmpArr;
  }

  public reset() {
    this.menu$.next([]);
  }

  public set(menu: Menu[]): Observable<Menu[]> {
    this.menu$.next(menu);
    return this.menu$.asObservable();
  }
}
